import {
  Archive,
  Check,
  CircleHelp,
  Ellipsis,
  EyeOff,
  Languages,
  type LucideIcon,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ICON } from "./ui";

// Shared dashboard building blocks. Native <dialog> gives modality, focus containment and Escape;
// we add focus on [data-autofocus] and focus return on unmount.

function useDialog(onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  // Captured during the first render, before the dialog takes focus. (Capturing it in the effect breaks
  // under StrictMode's mount → unmount → mount: the second capture would be an element inside the dialog.)
  const opener = useRef<HTMLElement | null>(null);
  if (opener.current === null && typeof document !== "undefined") {
    opener.current = document.activeElement as HTMLElement | null;
  }
  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (!d.open) d.showModal();
    d.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    return () => {
      // The opener can disappear with the change the dialog made (e.g. the discard button of a bar
      // that is gone after discarding); fall back to the main region instead of losing focus to <body>.
      setTimeout(() => {
        const back = opener.current;
        const target = back && document.contains(back) ? back : document.getElementById("main");
        target?.focus({ preventScroll: true });
      }, 0);
    };
  }, []);
  return {
    ref,
    onCancel: (e: React.SyntheticEvent) => {
      e.preventDefault();
      onClose();
    },
    onMouseDown: (e: React.MouseEvent) => {
      if (e.target === ref.current) onClose();
    },
  };
}

/**
 * When the focused control disappears (a bar's „Publică” after publishing, a removed product's menu),
 * browsers drop focus to <body> and keyboard users lose their place. Move it to the main region instead.
 */
export function useFocusRescue() {
  useEffect(() => {
    let last: Element | null = null;
    const onFocusIn = (e: FocusEvent) => {
      last = e.target as Element;
    };
    const observer = new MutationObserver(() => {
      if (last && !last.isConnected && document.activeElement === document.body) {
        last = null;
        document.getElementById("main")?.focus({ preventScroll: true });
      }
    });
    document.addEventListener("focusin", onFocusIn);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      observer.disconnect();
    };
  }, []);
}

export interface ModalProps {
  title: string;
  body?: ReactNode;
  alert?: boolean;
  onClose: () => void;
  children?: ReactNode;
  actions: ReactNode;
}

/** Centered dialog on desktop, bottom sheet on phones (prototype `D()` dialogs). */
export function Modal({ title, body, alert = false, onClose, children, actions }: ModalProps) {
  const { ref, onCancel, onMouseDown } = useDialog(onClose);
  const titleId = useId();
  const bodyId = useId();
  return (
    <dialog
      ref={ref}
      className="modal"
      role={alert ? "alertdialog" : undefined}
      aria-labelledby={titleId}
      aria-describedby={body ? bodyId : undefined}
      onCancel={onCancel}
      onMouseDown={onMouseDown}
    >
      <div className="modal-in">
        <h2 id={titleId} className="modal-title">
          {title}
        </h2>
        {body && (
          <p id={bodyId} className="modal-body">
            {body}
          </p>
        )}
        {children}
        <div className="modal-actions">{actions}</div>
      </div>
    </dialog>
  );
}

/** Right-hand panel (full screen on phones) used by the product editor. */
export function Drawer({
  title,
  sub,
  onClose,
  children,
  footer,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  const { ref, onCancel, onMouseDown } = useDialog(onClose);
  const titleId = useId();
  return (
    <dialog
      ref={ref}
      className="drawer"
      aria-labelledby={titleId}
      onCancel={onCancel}
      onMouseDown={onMouseDown}
    >
      <div className="drawer-in">
        <div className="drawer-head">
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <h2 id={titleId} className="modal-title">
              {title}
            </h2>
            {sub && <span className="caption muted">{sub}</span>}
          </div>
          <button type="button" className="icon-btn ghost" aria-label="Închide editorul" onClick={onClose}>
            <X size={22} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        <div className="drawer-foot">{footer}</div>
      </div>
    </dialog>
  );
}

export function Switch({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      className="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
    >
      <span className="track">
        <span className="knob" />
      </span>
    </button>
  );
}

export type ChipTone = "success" | "warning" | "neutral" | "info" | "error";

export function StatusChip({
  tone,
  icon: Icon,
  label,
  large = false,
}: {
  tone: ChipTone;
  icon: LucideIcon;
  label: string;
  large?: boolean;
}) {
  return (
    <span className={`st st-${tone}${large ? " lg" : ""}`}>
      <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
      {label}
    </span>
  );
}

/** Product/status chip catalogue from the prototype (CHIP). */
export const CHIPS = {
  published: { tone: "success", icon: Check, label: "Publicat" },
  draftOnly: { tone: "warning", icon: Pencil, label: "Ciornă · nepublicat" },
  offline: { tone: "neutral", icon: EyeOff, label: "Retras din public" },
  archived: { tone: "neutral", icon: Archive, label: "Arhivat" },
  pending: { tone: "warning", icon: Pencil, label: "Publicat · ciornă" },
  isNew: { tone: "warning", icon: Plus, label: "Nou · ciornă" },
  changed: { tone: "warning", icon: Pencil, label: "Modificat · ciornă" },
  alMissing: { tone: "warning", icon: CircleHelp, label: "Alergeni neconfirmați" },
  trNeeds: { tone: "warning", icon: RefreshCw, label: "EN · necesită actualizare" },
  trAuto: { tone: "info", icon: Languages, label: "EN · automată, nerevizuită" },
  trMissing: { tone: "neutral", icon: Languages, label: "EN · lipsă" },
  unavailable: { tone: "error", icon: X, label: "Indisponibil azi" },
  aiUnverified: { tone: "info", icon: Sparkles, label: "Generat cu AI, neverificat" },
  // Import review flags (Lot 3): shown until the restaurant edits or confirms the product.
  name_unclear: { tone: "warning", icon: CircleHelp, label: "Nume de verificat" },
  price_ambiguous: { tone: "warning", icon: CircleHelp, label: "Preț ambiguu" },
  price_missing: { tone: "warning", icon: CircleHelp, label: "Preț lipsă" },
  possible_duplicate: { tone: "warning", icon: CircleHelp, label: "Posibil duplicat" },
  portion_unclear: { tone: "warning", icon: CircleHelp, label: "Gramaj de verificat" },
  price_unverified: { tone: "warning", icon: CircleHelp, label: "Preț de confirmat" },
  import_new: { tone: "info", icon: CircleHelp, label: "Nou la import" },
  import_changed: { tone: "info", icon: CircleHelp, label: "Modificat la import" },
  import_missing: { tone: "warning", icon: CircleHelp, label: "Lipsește din import" },
} as const satisfies Record<string, { tone: ChipTone; icon: LucideIcon; label: string }>;

export interface MenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
}

/** Kebab menu with roving focus (arrows, Home/End), Escape returns focus to the trigger. */
export function PopMenu({
  label,
  items,
  header,
  trigger,
  triggerClass = "icon-btn",
}: {
  label: string;
  items: MenuItem[];
  header?: ReactNode;
  trigger?: ReactNode;
  triggerClass?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    wrap.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const outside = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);

  const close = () => {
    setOpen(false);
    setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = [...(wrap.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = list.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => {
      e.preventDefault();
      list[(n + list.length) % list.length]?.focus();
    };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(list.length - 1);
    else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") setOpen(false);
  };

  return (
    <div className="pop" ref={wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={() => setOpen(!open)}
      >
        {trigger ?? <Ellipsis {...ICON} aria-hidden="true" />}
      </button>
      {open && (
        <div className="pop-menu" role="menu" id={menuId} aria-label={label} onKeyDown={onKey}>
          {header}
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={it.danger ? "danger" : undefined}
              onClick={() => {
                // The trigger, not the menu item that is about to unmount, becomes the opener of any dialog.
                setOpen(false);
                triggerRef.current?.focus();
                it.onSelect();
              }}
            >
              <it.icon {...ICON} aria-hidden="true" />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Bar({
  tone,
  icon: Icon,
  title,
  sub,
  children,
  sticky = false,
}: {
  tone: "warning" | "success" | "neutral" | "info";
  icon: LucideIcon;
  title: string;
  sub?: string;
  children?: ReactNode;
  sticky?: boolean;
}) {
  return (
    <div className={`bar bar-${tone}${sticky ? " sticky" : ""}`} role="status">
      <Icon {...ICON} aria-hidden="true" />
      <div className="bt">
        <strong>{title}</strong>
        {sub && <span>{sub}</span>}
      </div>
      {children && (
        <div className="row" style={{ gap: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}
